import { Logger } from '@opentelemetry/api-logs';
import config from 'react-global-configuration';
import { Dispatch } from 'redux';

import { DISPLAY_ENV_KEY, ENVIRONMENTS_LIST } from '../constants/appConstants';
import { store } from '../Main';
import { ReduxState } from '../store';
import { openFailModal } from '../store/actions/errorModalActions';
import { extractStoreDetailsForTelemetry } from '../telemetry/Tracer';

export const LogLevel = {
  Info: 'info',
  Debug: 'debug',
  Warn: 'warning',
  Error: 'error',
} as const;

export type LogLevelValue = (typeof LogLevel)[keyof typeof LogLevel];

export default function recordLog(level: LogLevelValue, location: any, rawData?: any) {
  const logger = getLoggerFromStore();
  const processedData = removeSensitiveData(rawData);

  if (logger) {
    const details = extractStoreDetailsForTelemetry();
    (logger as Logger).emit({
      timestamp: Date.now(),
      body: { message: `${location}: ${processedData}`, extraInfo: details },
      severityText: level,
    });
  }
}

function removeSensitiveData(rawData: any): string {
  if (!rawData) {
    return '';
  }

  try {
    // Handle different data types appropriately
    let processedData: string;
    if (typeof rawData === 'string') {
      processedData = rawData;
    } else if (typeof rawData === 'object') {
      // For objects, convert to JSON string for better representation
      processedData = JSON.stringify(rawData, null, 2);
    } else {
      // For other types (number, boolean, etc.), convert to string
      processedData = String(rawData);
    }

    // Remove sensitive information more comprehensively
    return (
      processedData
        // Remove Authorization headers with various formats
        .replace(/Authorization:\s*[a-zA-Z0-9.-_]+/gi, 'Authorization: [REDACTED]')
        .replace(/Bearer\s+[a-zA-Z0-9.-_]+/gi, 'Bearer [REDACTED]')
        .replace(/Basic\s+[a-zA-Z0-9=]+/gi, 'Basic [REDACTED]')
        // Remove API keys and tokens
        .replace(/api[_-]?key[=:]\s*[a-zA-Z0-9.-_]+/gi, 'api_key=[REDACTED]')
        .replace(/token[=:]\s*[a-zA-Z0-9.-_]+/gi, 'token=[REDACTED]')
        // Remove passwords
        .replace(/password[=:]\s*[^\s&]+/gi, 'password=[REDACTED]')
        .replace(/pwd[=:]\s*[^\s&]+/gi, 'pwd=[REDACTED]')
    );
  } catch (e) {
    // Fallback: convert to string and apply basic sanitization
    return String(rawData)
      .replace(/Authorization:\s*[a-zA-Z0-9.-_]+/gi, 'Authorization: [REDACTED]')
      .replace(/Bearer\s+[a-zA-Z0-9.-_]+/gi, 'Bearer [REDACTED]');
  }
}

function getLoggerFromStore() {
  const state = store.getState() as ReduxState;
  return state.telemetryLogger;
}

/**
 * @param modalHeaderType is what the modal's header should read, also prefixes the logged message
 * @param modalBodyLocation is what the modal's body should contain, also will be location
 * @param errorObj is object: (make sure it is JSON), usually the error.toString()
 * @param extraInfo is anything else you want to pass, it will be tacked onto the object: string
 * @param consoleOnly only allows logs to be sent to the console
 * @returns {Promise<void>} a promise that can be ignored (if preferred)
 */
export function devErrorAndLog(
  modalHeaderType: string,
  modalBodyLocation: any,
  errorObj?: any,
  extraInfo?: string,
  consoleOnly = false,
) {
  return async (dispatch: Dispatch) => {
    // record error to telemetry
    recordLog(LogLevel.Error, `${modalHeaderType}: ${modalBodyLocation}`, errorObj);

    // show modal error if not production or demo
    const envKey = config.get(DISPLAY_ENV_KEY)
      ? config.get(DISPLAY_ENV_KEY).toString().toUpperCase()
      : 'LOCAL';
    if (ENVIRONMENTS_LIST.find(env => env === envKey)) {
      if (consoleOnly) {
        // Intentionally not logging to console in CI; rely on telemetry instead
        return;
      }
      if (envKey !== 'PROD' && envKey !== 'DEMO') {
        dispatch(openFailModal(modalHeaderType, modalBodyLocation.toString()));
      }
    }
  };
}
